import { createEffect, createSignal, getOwner, runWithOwner } from "solid-js"
import { BufferGeometry, MeshLambertMaterial, type Scene } from "three"
import { onCleanupRemoveAndDispose } from "../../../../../rx/cleanup/onCleanupRemoveAndDispose"
import useGridWidthHeight from "../../../../../rx/memo/useGridWidthHeight"
import { useNodeNeighborBaseFloorElevationReactiveMap } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import isIdListSame from "../../../../../rx/signal/equals/isIdListSame"
import type { Engine } from "../../../../core/engine/Engine.type"
import { EntityTypeId } from "../../../../core/entity/EntityTypeId.enum"
import type { Entity } from "../../../../core/entity/index"
import { createNewRNGConfig, nextNumberRange, next as rngNext, sample } from "../../../../core/rng/index"
import resetEntityRngConfig from "../../../../core/rng/resetEntityRngConfig"
import tmpRngWith from "../../../../core/rng/tmpRngWith"
import { immutableEmptyArray } from "../../../../lib"
import { VERTICES_PER_TILE } from "../../../consts"
import createBatchedMeshWithGeometryList from "../../../fn/createBatchedMeshWithGeometryList"
import createCamoMapTexture from "../../../fn/createCamoMapTexture"
import enableShadows from "../../../fn/enableShadows"
import setRandomYRotationQuaternion from "../../../fn/setRandomYRotationQuaternion"
import { dummyMatrix, dummyObject3D, dummyQuaternion, resetDummyObject3D } from "../../DummyObject3D/dummyObject3D"
import getTerrainMesh1Z from "../../TerrainMesh1V2/getTerrainMesh1Z"
import loadAndMergeRockGeometry, { type RockGeometrySets } from "./loadAndMergeRockGeometry"

type RockVariantChoice = {
  list: BufferGeometry[]
  // how strong we want to bias this palette for the etype
  weight: number
}

type RockPalette = {
  plain: RockVariantChoice
  moss: RockVariantChoice
  snow: RockVariantChoice
}

const tileRadius = VERTICES_PER_TILE / 2

function pickPalette(etype_id: EntityTypeId, rockSets: RockGeometrySets): RockPalette {
  // Bias palettes based on biome. Higher weight = more likely when sampling.
  switch (etype_id) {
    case EntityTypeId.Snow:
      return {
        plain: { list: rockSets.plain, weight: 1 },
        moss: { list: rockSets.moss, weight: 0.5 },
        snow: { list: rockSets.snow, weight: 3 },
      }
    case EntityTypeId.Forest:
    case EntityTypeId.Jungle:
    case EntityTypeId.Deadwood:
      return {
        plain: { list: rockSets.plain, weight: 1.25 },
        moss: { list: rockSets.moss, weight: 3 },
        snow: { list: rockSets.snow, weight: 0.25 },
      }
    case EntityTypeId.Mountain:
      return {
        plain: { list: rockSets.plain, weight: 2 },
        moss: { list: rockSets.moss, weight: 1 },
        snow: { list: rockSets.snow, weight: 1 },
      }
    case EntityTypeId.Desert:
      return {
        plain: { list: rockSets.plain, weight: 4 },
        moss: { list: rockSets.moss, weight: 0.25 },
        snow: { list: rockSets.snow, weight: 0.1 },
      }
    case EntityTypeId.Plain:
    default:
      return {
        plain: { list: rockSets.plain, weight: 2.5 },
        moss: { list: rockSets.moss, weight: 1.5 },
        snow: { list: rockSets.snow, weight: 0.2 },
      }
  }
}

function pickVariant(etype_id: EntityTypeId, rockSets: RockGeometrySets, rng: ReturnType<typeof createNewRNGConfig>) {
  const palette = pickPalette(etype_id, rockSets)
  const weights = [palette.plain.weight, palette.moss.weight, palette.snow.weight]
  const total = weights[0] + weights[1] + weights[2]
  const roll = nextNumberRange(0, total, rng)
  if (roll < weights[0]) {
    return sample(palette.plain.list, rng)!
  }
  if (roll < weights[0] + weights[1]) {
    return sample(palette.moss.list, rng)!
  }
  return sample(palette.snow.list, rng)!
}

function isEntityWithBoulder(ent: Entity) {
  const { id, etype_id } = ent
  if (etype_id === EntityTypeId.Desert) {
    return rngNext(tmpRngWith(id)) < 0.3
  }
  if (etype_id === EntityTypeId.Forest) {
    return rngNext(tmpRngWith(id)) < 0.3
  }
  if (etype_id === EntityTypeId.Jungle) {
    return rngNext(tmpRngWith(id)) < 0.3
  }
  if (etype_id === EntityTypeId.Plain) {
    return rngNext(tmpRngWith(id)) < 0.1
  }
  if (etype_id === EntityTypeId.Mountain) {
    return rngNext(tmpRngWith(id)) < 0.5
  }
  if (etype_id === EntityTypeId.River) {
    return rngNext(tmpRngWith(id)) < 0.1
  }
  if (etype_id === EntityTypeId.Sea) {
    return rngNext(tmpRngWith(id)) < 0.2
  }
  if (etype_id === EntityTypeId.Shoal) {
    return rngNext(tmpRngWith(id)) < 0.3
  }
  if (etype_id === EntityTypeId.Snow) {
    return rngNext(tmpRngWith(id)) < 0.35
  }
  return false
}

const lowResBoulderNoise = createCamoMapTexture(tmpRngWith(1), 32, 8)

export default function createBoulderInstancedMeshV2QuaterniusEffect(
  engine: Engine,
  scene: Scene,
): void {
  const neighborBaseElevationReactiveMap = useNodeNeighborBaseFloorElevationReactiveMap(engine)
  const getGridSize = useGridWidthHeight(engine)

  const [getEnts, setEnts] = createSignal<Array<Entity>>(immutableEmptyArray as unknown as Array<Entity>, {
    equals: isIdListSame,
  })
  createEffect(() => {
    setEnts(engine.state.ents.filter(isEntityWithBoulder))
  })

  const owner = getOwner()
  loadAndMergeRockGeometry().then((rockSets) => {
    runWithOwner(owner, () => {
      createEffect(() => {
        if (neighborBaseElevationReactiveMap.size === 0) return

        const ents = getEnts()
        if (ents.length === 0) return

        const owner = getOwner()
        const gridWidth = getGridSize().width

        const material = new MeshLambertMaterial({
          map: lowResBoulderNoise,
          vertexColors: true,
        })

        // collect all geometries we might use this frame
        const allGeometries = [...rockSets.plain, ...rockSets.moss, ...rockSets.snow]

        // Placement logic mirrors V1: random jitter within tile, height from neighbor map
        const geometryIds: number[] = []
        const rng = createNewRNGConfig()

        resetDummyObject3D()

        const batchSize = ents.length
        const batchedMesh = createBatchedMeshWithGeometryList(batchSize, allGeometries, material)

        for (let i = 0; i < allGeometries.length; i++) {
          geometryIds[i] = batchedMesh.addGeometry(allGeometries[i])
        }

        for (let i = 0; i < ents.length; i++) {
          const ent = ents[i]
          const { id, etype_id, x, y } = ent

          resetEntityRngConfig(id, rng)
          const nodeIndex = x + y * gridWidth
          const baseElevationsCollection = neighborBaseElevationReactiveMap.get(nodeIndex)
          const tileX = (nextNumberRange(-tileRadius, tileRadius, rng) * 0.9)
          const tileY = (nextNumberRange(-tileRadius, tileRadius, rng) * 0.9)
          if (baseElevationsCollection) {
            const elevationBase = getTerrainMesh1Z(baseElevationsCollection, tileX, tileY)
            // const elevation = elevationBase + nextNumberRange(-0.05, 0, rng)
            const elevation = elevationBase

            const wx = x * VERTICES_PER_TILE + tileX
            const wy = y * VERTICES_PER_TILE + tileY

            setRandomYRotationQuaternion(tmpRngWith(id), dummyQuaternion)
            dummyObject3D.position.set(wx, elevation, wy)
            dummyObject3D.quaternion.copy(dummyQuaternion)
            dummyObject3D.updateMatrix()

            const geometry = pickVariant(etype_id, rockSets, rng)
            const geometryIndex = allGeometries.indexOf(geometry)
            const instanceId = batchedMesh.addInstance(geometryIds[geometryIndex])
            batchedMesh.setMatrixAt(instanceId, dummyMatrix)
          }
        }

        enableShadows(batchedMesh)
        scene.add(batchedMesh)
        runWithOwner(owner, () => {
          onCleanupRemoveAndDispose(batchedMesh)
        })
      })
    })
  })
}
